import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import { getLayouts, isLoading } from "@app/scene/settings/component/FormLayouts/store";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import ConfirmLayoutDeleteModal from "../ConfirmLayoutDeleteModal";

import styles from "./DeleteLayoutAction.module.scss";

interface Props {
  analyticsIssuer?: AnalyticsService;
  disabled?: boolean;
  id: FormLayout["id"];
  layouts: FormLayout[];
  loading: boolean;
  showIcon?: boolean;
  showLabel?: boolean;
}

const messages = defineMessages({
  delete: {
    id: "settings.formLayouts.deleteLayout.delete",
    defaultMessage: "Delete",
    description: "Delete button title",
  },
  deleteLayout: {
    id: "settings.formLayouts.deleteLayout.deleteLayout",
    defaultMessage: "Delete Layout",
    description: "Delete Layout button tooltip",
  },
  onlyLayout: {
    id: "settings.formLayouts.deleteLayout.onlyLayout",
    defaultMessage: "Cannot delete only layout. Please create another layout to delete this one.",
    description: "Only layout delete button tooltip",
  },
  tooltip: {
    id: "settings.formLayouts.deleteLayout.tooltip",
    defaultMessage: "Delete Layout",
    description: "Delete Layout tooltip",
  },
  usedLayout: {
    id: "settings.formLayouts.deleteLayout.usedLayout",
    defaultMessage:
      "You cannot delete layouts that are currently assigned to users. Please assign another layout to the roles that currently have this layout assigned before attempting to delete it.",
    description: "Used layout delete button tooltip",
  },
});

const DeleteLayoutAction: React.FC<Props> = ({
  id,
  analyticsIssuer,
  disabled,
  layouts,
  loading,
  showIcon,
  showLabel,
}) => {
  const intl = useIntl();

  const [confirmationVisible, showConfirmation, hideConfirmation] = useBoolean(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const isOnly = layouts.length < 2;
  const isUsed = !!layouts.find((layout) => layout.id === id)?.roles?.length;

  const isDisabled = disabled || loading || isOnly || isUsed;

  const handleDeleteClick = useCallback(() => {
    analyticsIssuer?.clicked(["Individual Layout", "Delete Layout"]);

    setTooltipVisible(false);
    showConfirmation();
  }, [analyticsIssuer, setTooltipVisible, showConfirmation]);

  return (
    <div className={styles.container}>
      <Tooltip
        onVisibleChange={setTooltipVisible}
        overlayClassName={styles.overlay}
        placement={isOnly || isUsed ? "topRight" : "top"}
        title={
          isOnly
            ? intl.formatMessage(messages.onlyLayout)
            : isUsed
            ? intl.formatMessage(messages.usedLayout)
            : showIcon
            ? intl.formatMessage(messages.tooltip)
            : null
        }
        trigger={disabled || confirmationVisible || !showIcon ? [] : ["hover"]}
        visible={tooltipVisible}
      >
        {showIcon ? (
          <Button
            className={cn({ [styles.active]: confirmationVisible })}
            disabled={isDisabled}
            icon={<FontAwesomeIcon icon={faTrash} />}
            iconOnly
            onClick={handleDeleteClick}
            type="text"
          />
        ) : showLabel ? (
          <span
            className={cn({ [styles.deleteText]: !isDisabled, [styles.disabledText]: isDisabled })}
            onClick={isDisabled ? undefined : handleDeleteClick}
          >
            {intl.formatMessage(messages.deleteLayout)}
          </span>
        ) : (
          <Button danger disabled={isDisabled} onClick={handleDeleteClick} type="default">
            {intl.formatMessage(messages.delete)}
          </Button>
        )}
      </Tooltip>

      {confirmationVisible && <ConfirmLayoutDeleteModal id={id} onHide={hideConfirmation} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  layouts: getLayouts(state),
  loading: isLoading(state),
});

export default connect(mapStateToProps)(DeleteLayoutAction);
